import { motion } from "framer-motion";
import "../../css/iaat/IAATKeyAreas.css"; // Importing the CSS file
import Header from '../Header';
import Footer from '../Footer';

const keyAreas = [
    {
      title: "Pre-Algebraic Number Skills",
      description: "Tests basic math concepts necessary for Algebra success.",
      questions: [
        {
          q: "What is the least common multiple (LCM) of 6 and 8?",
          options: { a: "12", b: "18", c: "24", d: "36" },
          correct: "c",
          explanation: "The least common multiple of two numbers is the smallest number that is a multiple of both. The LCM of 6 and 8 is 24."
        },
        {
          q: "Simplify: 3/4 + 2/5",
          options: { a: "19/20", b: "23/20", c: "1", d: "5/9" },
          correct: "b",
          explanation: "Find a common denominator (20), then add the fractions: (15/20 + 8/20 = 23/20)."
        },
        {
          q: "If 2x + 5 = 15, what is x?",
          options: { a: "4", b: "5", c: "6", d: "7" },
          correct: "b",
          explanation: "Solving for x: Subtract 5 from both sides (2x = 10), then divide by 2 (x = 5)."
        },
      ],
    },
    {
      title: "Interpreting Mathematical Information",
      description: "Measures students' ability to analyze data from graphs or text.",
      questions: [
        {
          q: "A graph shows the number of students in a class over 5 years. What is the trend?",
          options: { a: "Increasing", b: "Decreasing", c: "Constant", d: "Fluctuating" },
          correct: "a",
          explanation: "If the number of students is consistently rising, the trend is increasing."
        },
        {
          q: "If a bus travels 50 miles in 2 hours, what is its average speed?",
          options: { a: "20 mph", b: "25 mph", c: "30 mph", d: "50 mph" },
          correct: "b",
          explanation: "Speed = Distance ÷ Time. 50 miles ÷ 2 hours = 25 mph."
        },
        {
          q: "Which chart type is best for comparing percentages?",
          options: { a: "Bar Chart", b: "Line Graph", c: "Pie Chart", d: "Histogram" },
          correct: "c",
          explanation: "Pie charts visually show proportions and percentages of a whole."
        },
      ],
    },
    {
      title: "Representing Relationships",
      description: "Evaluates the ability to recognize patterns and express numerical relationships.",
      questions: [
        {
          q: "What comes next in the sequence? 2, 4, 8, 16, __?",
          options: { a: "24", b: "30", c: "32", d: "36" },
          correct: "c",
          explanation: "Each term is multiplied by 2. 16 × 2 = 32."
        },
        {
          q: "If y = 3x + 2, find y when x = 5.",
          options: { a: "15", b: "17", c: "18", d: "20" },
          correct: "b",
          explanation: "Substituting x = 5: y = 3(5) + 2 = 17."
        },
        {
          q: "Identify the pattern: A1, B2, C3, __?",
          options: { a: "D4", b: "E5", c: "F6", d: "G7" },
          correct: "a",
          explanation: "The letters follow A → B → C → D, and the numbers follow 1 → 2 → 3 → 4."
        },
      ],
    },
    {
      title: "Using Symbols",
      description: "Assesses understanding of algebraic symbols and their correct usage in equations.",
      questions: [
        {
          q: "Solve for x: 5(x - 3) = 20.",
          options: { a: "6", b: "7", c: "8", d: "9" },
          correct: "b",
          explanation: "Expanding: 5x - 15 = 20. Adding 15: 5x = 35. Dividing by 5: x = 7."
        },
        {
          q: "Which symbol represents ‘greater than or equal to’?",
          options: { a: ">", b: "<", c: "≥", d: "≤" },
          correct: "c",
          explanation: "The symbol '≥' means 'greater than or equal to'."
        },
        {
          q: "What is the correct order of operations in an equation?",
          options: { a: "PMDAS", b: "DMPAS", c: "PEMDAS", d: "PADMS" },
          correct: "c",
          explanation: "PEMDAS stands for Parentheses, Exponents, Multiplication/Division, Addition/Subtraction."
        },
      ],
    },
  ];

export default function IAATKeyAreas() {
  return (
    <div>

    <div className="iaat-container">
      {/* SEO Optimization */}
      <h1 className="iaat-title">
        IAAT Key Areas & Sample Questions
      </h1>
      <p className="iaat-subtitle">
        Learn about the essential areas assessed in the Iowa Algebra Aptitude Test (IAAT) and practice with sample questions.
      </p>

      {/* Key Areas Section */}
      <div className="iaat-section">
      {keyAreas.map((area, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="iaat-card"
        >
          <h2 className="iaat-heading">{area.title}</h2>
          <p className="iaat-description">{area.description}</p>

          {/* Sample Questions */}
          <div className="iaat-questions">
            {area.questions.map((item, qIndex) => (
              <div key={qIndex} className="iaat-question-box">
                {/* Question */}
                <h3 className="iaat-question">{item.q}</h3>

                {/* Answer Options (Displayed in a single row) */}
                <div className="iaat-options">
                  {Object.entries(item.options).map(([key, value]) => (
                    <span key={key} className="iaat-option">
                      <strong>{key.toUpperCase()}.</strong> {value} {"            "} 
                    </span>
                  ))}
                </div>

                {/* Correct Answer */}
                <p className="iaat-correct-answer">
                  <strong>Correct Answer:</strong> {item.correct.toUpperCase()}
                </p>

                {/* Explanation (With spacing) */}
                <p className="iaat-explanation">
                  <strong>Explanation:</strong> {item.explanation}
                </p>
              </div>
            ))}
          </div>
        </motion.div>
      ))}
      </div>
    </div>

    </div>
  );
}
